// Added for NYCE.LOGIC (not in @ophub/ui): Idempotency-Key handling for commands.
import { ApiError } from './errors';

export const IDEMPOTENCY_HEADER = 'Idempotency-Key';

export function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto && globalThis.isSecureContext)
    return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** Network failures (no response) and 5xx may be retried with the same key; anything else may not. */
export function isRetryable(e: unknown): boolean {
  if (e instanceof ApiError) return e.status >= 500;
  return e instanceof TypeError; // fetch rejects with TypeError when the server cannot be reached
}

/**
 * Tracks the idempotency key for one kind of user action. The same key is reused while the user retries an identical
 * payload after a retryable failure; anything else gets a new key.
 */
export class IdempotentAction {
  private pending: { fingerprint: string; key: string } | null = null;

  keyFor(payload: unknown): string {
    const fingerprint = JSON.stringify(payload ?? null);
    if (!this.pending || this.pending.fingerprint !== fingerprint)
      this.pending = { fingerprint, key: uuid() };
    return this.pending.key;
  }

  /** Call after the attempt: keep the key only if the failure is retryable. */
  settle(error?: unknown): void {
    if (!isRetryable(error)) this.pending = null;
  }

  async run<T>(payload: unknown, fn: (key: string) => Promise<T>): Promise<T> {
    const key = this.keyFor(payload);
    try {
      const result = await fn(key);
      this.settle();
      return result;
    } catch (e) {
      this.settle(e);
      throw e;
    }
  }
}
